import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Edit,
  Eye,
  Plus,
  Lock,
  Book,
  Clock,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';
import permissionsService from '../../services/permissionsService';
import ReviewWorkflowActions from '../AssessmentBuilder/ReviewWorkflowActions';
import StudyDraftDetailModal from './StudyDraftDetailModal';
import { APPROVE_STUDY_PERMISSION } from '../../services/reviewWorkflowApi';
import PageHeader from '../Shared/PageHeader';
import { EmptyState, PageLoader, Switch } from '../ui';

// -- PARENT COMPONENT: StudyList --
// Logged-in user id, used to scope review-workflow actions (submitter-only).
const getCurrentUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.id ?? u?.user_id ?? u?.pk ?? null;
  } catch {
    return null;
  }
};

// Single status badge for study drafts. `review_status` is the source of truth;
// a draft with no review_status yet falls back to the `draft` entry. Completion
// (`status`: incomplete/complete) is shown as muted text, not a competing badge.
const REVIEW_BADGE = {
  draft: { label: 'Draft', cls: 'badge-gray' },
  in_review: { label: 'Pending Review', cls: 'badge-info' },
  approved: { label: 'Approved', cls: 'bg-teal-50 text-teal-700 ring-teal-600/20 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-400/25' },
  denied: { label: 'Needs Revision', cls: 'badge-danger' },
  published: { label: 'Published', cls: 'badge-success' },
  // Retired version replaced by a published amendment (FECOA-1831). Hidden from
  // the default list; visible via the "Show superseded versions" toggle.
  superseded: { label: 'Superseded', cls: 'badge-gray' },
};

// Show the version chip only for drafts that are part of an amendment chain;
// standalone v1 drafts stay chip-free so the pre-amendment UI is unchanged.
const isChainMember = (draft) =>
  draft?.amended_from != null ||
  (draft?.version_number ?? 1) > 1 ||
  !!draft?.has_open_amendment ||
  draft?.review_status === 'superseded';

// `embedded`: shown inside the Study Builder home, which has its own header.
function StudyList({ embedded = false }) {
  const navigate = useNavigate();
  const currentUserId = getCurrentUserId();
  const [canApproveStudy, setCanApproveStudy] = useState(false);
  const [studyDrafts, setStudyDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Draft whose full design is open in the detail modal. Stored by id and
  // resolved against the live list so the 30s silent refetch keeps the modal
  // header (e.g. review status) current instead of pinning a stale object.
  const [detailDraftId, setDetailDraftId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
  // Superseded versions are history, hidden by default (FECOA-1831).
  const [showSuperseded, setShowSuperseded] = useState(false);

  // silent=true refreshes the list in the background (no spinner, no error
  // toast) so polling/focus refetches don't flicker the table or interrupt.
  const loadStudyDrafts = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await studyBuilderService.getStudyDrafts();
      setStudyDrafts(response || []);
    } catch (error) {
      console.error('Error loading drafts:', error);
      if (!silent) toast.error('Failed to load drafts');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStudyDrafts();
  }, [loadStudyDrafts]);

  // Keep the list roughly live across users: silent refetch every 30s (matching
  // the notification bell's cadence) and whenever the tab regains focus, so a
  // submission/approval on another machine shows up without a manual refresh.
  useEffect(() => {
    const tick = () => loadStudyDrafts(true);
    const interval = setInterval(tick, 30000);
    const onFocus = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [loadStudyDrafts]);

  // Whether the current user can approve study drafts — enables inline approve/deny.
  useEffect(() => {
    let active = true;
    permissionsService
      .hasPermission(APPROVE_STUDY_PERMISSION)
      .then((ok) => { if (active) setCanApproveStudy(!!ok); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const handleEditClick = useCallback((draft) => {
    localStorage.removeItem('studyDrafts');
    localStorage.setItem('currentStudyId', String(draft.id));
    localStorage.setItem('currentStudyDraftId', String(draft.id));

    studyBuilderService.getStudyDrafts().then((resp) => {
      setStudyDrafts(resp);
    });

    navigate(`/studybuilder/${encodeURIComponent(draft.name)}`);
  }, [navigate]);

  // Open a submitted draft in review mode: same builder load as editing, but
  // lands on the review route which pins the Approve/Deny bar to the bottom.
  const handleReviewClick = useCallback((draft) => {
    localStorage.removeItem('studyDrafts');
    localStorage.setItem('currentStudyId', String(draft.id));
    localStorage.setItem('currentStudyDraftId', String(draft.id));
    navigate(`/studybuilder/review/${encodeURIComponent(draft.name)}`, {
      state: { reviewEntity: draft },
    });
  }, [navigate]);

  const formatDate = useMemo(() => (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const handleSort = useCallback((key) => {
    setSortConfig((prevConfig) => {
      // If clicking the same column, toggle direction
      if (prevConfig.key === key) {
        return { 
          ...prevConfig, 
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' 
        };
      }
      // If clicking a new column, default to ascending
      return { key, direction: 'asc' };
    });
  }, []);

  // Count of hidden superseded versions, for the toggle label.
  const supersededCount = useMemo(
    () => (Array.isArray(studyDrafts) ? studyDrafts : []).filter(
      (d) => d?.review_status === 'superseded',
    ).length,
    [studyDrafts],
  );

  const sortedStudyDrafts = useMemo(() => {
    // Ensure studyDrafts is an array
    const draftsArray = Array.isArray(studyDrafts) ? studyDrafts :
                       (studyDrafts && typeof studyDrafts === 'object' ? Object.values(studyDrafts) : []);

    console.log('Draft array after conversion:', draftsArray);
    // Superseded versions are filtered out of the default view (FECOA-1831);
    // the chain stays visible from the draft detail modal.
    const draftsToSort = draftsArray.filter(
      (d) => showSuperseded || d?.review_status !== 'superseded',
    );
    console.log('Drafts to sort:', draftsToSort);
    if (sortConfig.key) {
      draftsToSort.sort((a, b) => {
        // First check if a or b are null/undefined
        if (!a) return 1;
        if (!b) return -1;
        
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle special cases (null, undefined, formatting)
        if (sortConfig.key === 'status') {
          // The STATUS column displays the review_status badge, so sort by that label
          aValue = REVIEW_BADGE[a.review_status || 'draft']?.label || '';
          bValue = REVIEW_BADGE[b.review_status || 'draft']?.label || '';
        }
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        // String comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Numeric or date comparison
        try {
          return sortConfig.direction === 'asc' 
            ? (aValue > bValue ? 1 : -1) 
            : (bValue > aValue ? 1 : -1);
        } catch (error) {
          console.error('Error during sorting comparison:', error);
          return 0; // Return equal if comparison fails
        }
      });
    }
    return draftsToSort;
  }, [studyDrafts, sortConfig, showSuperseded]);

  return (
    <div>
      <div>
        {!embedded && (
          <>
            <button type="button" onClick={() => navigate('/studybuilder')} className="back-link">
              <ChevronLeft className="h-4 w-4" />
              Back to Study Builder
            </button>
            <PageHeader
              icon={Book}
              title="Study Drafts"
              subtitle="Study designs in progress, in review and published."
            />
          </>
        )}

        {loading ? (
          <PageLoader className="py-16" />
        ) : studyDrafts.length > 0 ? (
          <div className="card overflow-hidden">
           <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
             <div className="flex items-center gap-2">
               <h2 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Study drafts</h2>
               <span className="badge badge-gray tabular-nums">{sortedStudyDrafts.length}</span>
             </div>
             {supersededCount > 0 && (
               <label className="flex cursor-pointer select-none items-center gap-2.5 text-[13px] text-gray-600 dark:text-gray-300">
                 <Switch
                   checked={showSuperseded}
                   onChange={(e) => setShowSuperseded(e.target.checked)}
                 />
                 Show superseded versions ({supersededCount})
               </label>
             )}
           </div>
           <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-y border-gray-100 dark:border-white/[0.06]">
                  <th 
                    className="th-cell group cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      <span>Study Name</span>
                      <div className="ml-1">
                        {sortConfig.key === 'name' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                          ) : (
                            <ArrowDown className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                          )
                        ) : (
                          <div className="h-3 w-3 opacity-0 group-hover:opacity-60">
                            <ArrowUp className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th 
                    className="th-cell group cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      <span>Status</span>
                      <div className="ml-1">
                        {sortConfig.key === 'status' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                          ) : (
                            <ArrowDown className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                          )
                        ) : (
                          <div className="h-3 w-3 opacity-0 group-hover:opacity-60">
                            <ArrowUp className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th 
                    className="th-cell group cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100"
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="flex items-center">
                      <span>Last Updated</span>
                      <div className="ml-1">
                        {sortConfig.key === 'updated_at' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                          ) : (
                            <ArrowDown className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                          )
                        ) : (
                          <div className="h-3 w-3 opacity-0 group-hover:opacity-60">
                            <ArrowUp className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="th-cell text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.06]">
                {sortedStudyDrafts.length > 0 ? (
                  sortedStudyDrafts
                    .filter(draft => {
                      console.log('Checking draft for rendering:', draft);
                      return draft && typeof draft === 'object';
                    })
                    .map((draft) => (
                      <React.Fragment key={draft?.id || Math.random()}>
                        <tr className="tr-interactive">
                          <td className="td-cell whitespace-nowrap">
                            <div className="flex min-w-0 items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  ['in_review', 'approved', 'published', 'superseded'].includes(draft.review_status)
                                    ? setDetailDraftId(draft.id)
                                    : handleEditClick(draft)
                                }
                                className="truncate text-left font-medium text-gray-900 hover:text-primary-700 hover:underline dark:text-gray-50 dark:hover:text-primary-300"
                              >
                                {draft.name}
                              </button>
                              {isChainMember(draft) && (
                                <span
                                  className="code-chip flex-shrink-0"
                                  title={`Design version ${draft.version_number ?? 1}`}
                                >
                                  v{draft.version_number ?? 1}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="td-cell whitespace-nowrap">
                            {(() => {
                              // Single status badge driven by review_status (defaults
                              // to Draft). Completion is muted text, not a second badge,
                              // and only matters while the draft is still editable.
                              const rs = draft.review_status || 'draft';
                              const badge = REVIEW_BADGE[rs] || REVIEW_BADGE.draft;
                              const showCompletion = rs === 'draft' || rs === 'denied';
                              const completion = draft.completion;
                              const isComplete = completion && completion.done >= completion.total;
                              return (
                                <div className="flex items-center gap-3">
                                  <span
                                    className={`badge badge-dot ${badge.cls}`}
                                  >
                                    {badge.label}
                                  </span>
                                  {showCompletion && completion && (
                                    <span className="flex items-center gap-2">
                                      <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-gray-100 sm:block dark:bg-white/[0.08]" aria-hidden="true">
                                        <span
                                          className="block h-full rounded-full bg-primary-500"
                                          style={{ width: `${completion.total ? Math.min(100, (completion.done / completion.total) * 100) : 0}%` }}
                                        />
                                      </span>
                                      <span
                                        className={`text-xs tabular-nums ${isComplete ? 'text-gray-500 dark:text-gray-400' : 'text-warning-700 dark:text-warning-400'}`}
                                      >
                                        {completion.done}/{completion.total} complete
                                      </span>
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="td-cell whitespace-nowrap">
                            <div className="flex items-center text-gray-500 dark:text-gray-400">
                              <Clock className="mr-1.5 h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                              <span className="tabular-nums">{formatDate(draft.updated_at)}</span>
                            </div>
                          </td>
                          <td className="td-cell whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <ReviewWorkflowActions
                                entity={draft}
                                entityType="study"
                                currentUserId={currentUserId}
                                isApprover={canApproveStudy}
                                onChanged={loadStudyDrafts}
                                onReview={handleReviewClick}
                              />
                              {['in_review', 'approved', 'published', 'superseded'].includes(draft.review_status) ? (
                                <span
                                  className="inline-flex h-8 w-8 cursor-not-allowed items-center justify-center text-gray-300 dark:text-gray-600"
                                  title={
                                    draft.review_status === 'published'
                                      ? 'Locked. Published designs are read-only; create an amendment to make changes.'
                                      : draft.review_status === 'superseded'
                                        ? 'Locked. This version was superseded by a newer published design and is kept as part of the study record.'
                                        : 'Locked. Withdraw the submission or revoke the approval to edit it.'
                                  }
                                >
                                  <Lock className="h-4 w-4" />
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleEditClick(draft)}
                                  className="btn btn-secondary btn-sm"
                                  title="Edit Draft"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                  Open
                                </button>
                              )}
                              <button
                                onClick={() => setDetailDraftId(draft.id)}
                                className="btn btn-ghost btn-sm btn-icon text-gray-500 dark:text-gray-400"
                                title="View design details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))
                ) : (
                  <tr>
                    <td colSpan={4}>
                      <EmptyState
                        icon={Book}
                        title="No study drafts found"
                        description="Create your first study to get started"
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
           </div>
          </div>
        ) : (
          <div className="card">
            <EmptyState
              icon={Book}
              title="No study drafts found"
              description="Create your first study to get started"
              action={
                <button type="button" onClick={() => navigate('/studybuilder/create')} className="btn btn-primary">
                  <Plus className="h-4 w-4" />
                  Create Study
                </button>
              }
            />
          </div>
        )}
      </div>

      {(() => {
        const detailDraft = studyDrafts.find((d) => d?.id === detailDraftId) || null;
        // Version chain for the detail modal (FECOA-1831): every draft sharing
        // the selected draft's name, oldest version first. Includes superseded
        // versions even when they are hidden from the table.
        const chain = detailDraft
          ? studyDrafts
              .filter((d) => d?.name === detailDraft.name)
              .sort((a, b) => (a.version_number ?? 1) - (b.version_number ?? 1))
          : [];
        return (
          <StudyDraftDetailModal
            open={detailDraftId !== null}
            draft={detailDraft}
            chain={chain}
            onClose={() => setDetailDraftId(null)}
          />
        );
      })()}
    </div>
  );
}

export default StudyList;